<script setup>
import { computed } from 'vue'
import { usePage, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import BrandLockup from '@/Components/UI/BrandLockup.vue'
import Icon from '@/Components/UI/Icon.vue'

const props = defineProps({
  // supplier | vendor | host | driver | partner
  portal: { type: String, default: 'partner' },
})

// One shell, five personalities. Glyphs reuse Icon.vue; tints are color-mix
// blends of the brand Sunset(--accent)→Lagoon(--second) gradient.
// Five depths of the same sea (2026-09 restyle): no more coral/amber panels.
const PORTALS = {
  supplier: { glyph: 'rental',  tint: 'linear-gradient(150deg, var(--azure) 0%, color-mix(in oklch, var(--azure) 60%, var(--night)) 100%)' },
  vendor:   { glyph: 'compass', tint: 'linear-gradient(150deg, color-mix(in oklch, var(--second) 70%, var(--azure)) 0%, var(--azure) 100%)' },
  host:     { glyph: 'home',    tint: 'linear-gradient(155deg, color-mix(in oklch, var(--azure) 55%, var(--night)) 0%, var(--night) 100%)' },
  driver:   { glyph: 'truck',   tint: 'linear-gradient(150deg, var(--azure) 0%, color-mix(in oklch, var(--second) 50%, var(--azure)) 100%)' },
  partner:  { glyph: 'sun',     tint: 'linear-gradient(150deg, color-mix(in oklch, var(--second) 55%, var(--azure)) 0%, color-mix(in oklch, var(--azure) 65%, var(--night)) 100%)' },
}
// Every customer-facing surface speaks the same four languages.
const LOCALES = [
  { value: 'en', label: 'EN' },
  { value: 'ru', label: 'RU' },
  { value: 'zh', label: '中文' },
  { value: 'th', label: 'ไทย' },
]

const cfg = computed(() => PORTALS[props.portal] || PORTALS.partner)

const page = usePage()
const flash = computed(() => page.props.flash || {})
const currentLocale = computed(() => page.props.locale || 'en')
const { t } = useI18n()

function setLang(lang) {
  if (lang === currentLocale.value) return
  router.get(window.location.pathname, { lang }, { preserveScroll: true, preserveState: false })
}
</script>

<template>
  <div class="pas public-shell">
    <aside
      class="pas__panel"
      :style="{ background: cfg.tint }"
    >
      <div class="pas__top">
        <span class="pas__lock"><BrandLockup /></span>
        <div class="pas__lang">
          <template v-for="(loc, i) in LOCALES" :key="loc.value">
            <span v-if="i" class="pas__lang-sep">·</span>
            <button
              :class="['pas__lang-btn', currentLocale === loc.value && 'is-active']"
              type="button"
              @click="setLang(loc.value)"
            >{{ loc.label }}</button>
          </template>
        </div>
      </div>

      <Icon
        :name="cfg.glyph"
        :size="190"
        :stroke="1.4"
        class="pas__glyph"
      />

      <div class="pas__copy">
        <h1 class="pas__heading">{{ t(`portal_auth.${portal}.heading`) }}</h1>
        <p class="pas__sub">{{ t(`portal_auth.${portal}.sub`) }}</p>
      </div>
    </aside>

    <main class="pas__form">
      <div class="pas__form-inner">
        <p class="pas__cap">{{ t(`portal_auth.${portal}.cap`) }}</p>
        <div
          v-if="flash.alert"
          class="pas__flash pas__flash--alert"
          role="alert"
        >
          <Icon name="alert" :size="16" /><span>{{ flash.alert }}</span>
        </div>
        <div
          v-if="flash.notice"
          class="pas__flash pas__flash--notice"
        >{{ flash.notice }}</div>
        <slot />
      </div>
    </main>
  </div>
</template>

<style scoped>
.pas { min-height: 100vh; display: grid; grid-template-columns: 46% 1fr; background: var(--bg); }

.pas__panel { position: relative; overflow: hidden; color: var(--accentInk); display: flex; flex-direction: column; justify-content: space-between; gap: 1rem; padding: clamp(1.5rem, 3vw, 2.5rem); padding-top: max(env(safe-area-inset-top), clamp(1.5rem, 3vw, 2.5rem)); }
.pas__top { display: flex; align-items: center; justify-content: space-between; gap: 1rem; position: relative; z-index: 2; }
.pas__lock :deep(.brand-lockup__sun) { color: var(--accentInk) !important; }
.pas__lock :deep(.brand-lockup__word) { color: var(--accentInk) !important; }
.pas__lang { display: flex; align-items: center; gap: 0.25rem; }
.pas__lang-btn { background: transparent; border: 0; color: color-mix(in oklch, var(--accentInk) 65%, transparent); font-size: 0.75rem; font-weight: 600; cursor: pointer; padding: 0.1rem 0.2rem; letter-spacing: 0.03em; }
.pas__lang-btn.is-active { color: var(--accentInk); }
.pas__lang-sep { color: color-mix(in oklch, var(--accentInk) 45%, transparent); font-size: 0.75rem; }
.pas__glyph { position: absolute; right: -2.5rem; top: 50%; transform: translateY(-50%); color: var(--accentInk); opacity: 0.16; pointer-events: none; }
.pas__copy { position: relative; z-index: 2; }
.pas__heading { font-family: var(--display); font-weight: 500; font-size: clamp(1.9rem, 2.8vw, 2.6rem); line-height: 1.08; letter-spacing: -0.005em; margin: 0 0 0.5rem; }
.pas__sub { font-size: 0.95rem; line-height: 1.5; opacity: 0.92; margin: 0; max-width: 22rem; }

.pas__form { display: grid; place-items: center; padding: clamp(1.5rem, 4vw, 3rem); }
.pas__form-inner { width: 100%; max-width: 23rem; }
.pas__cap { font-size: 0.7rem; letter-spacing: var(--eyebrow-tracking, 0.18em); text-transform: uppercase; font-weight: 600; color: color-mix(in oklch, var(--gold, var(--warn)) 62%, var(--ink)); margin: 0 0 1rem; }
.pas__flash { display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem 1rem; margin-bottom: 0.75rem; border-radius: var(--radius-input, 0.5rem); font-size: 0.9375rem; }
.pas__flash :deep(svg) { flex-shrink: 0; }
.pas__flash--alert { background: color-mix(in oklch, var(--danger) 12%, var(--surface)); color: var(--danger); }
.pas__flash--notice { background: color-mix(in oklch, var(--ok) 12%, var(--surface)); color: var(--ok); }

@media (max-width: 767px) {
  .pas { grid-template-columns: 1fr; grid-template-rows: auto 1fr; }
  .pas__panel { gap: 0.75rem; padding: max(env(safe-area-inset-top), 1.1rem) 1.25rem 1.25rem; }
  .pas__heading { font-size: 1.45rem; }
  .pas__sub { display: none; }
  .pas__glyph { width: 92px; height: 92px; right: -0.75rem; top: 62%; opacity: 0.2; }
  .pas__form { place-items: start center; }
}
</style>
